import * as React from "react";
import { IoRefresh } from "react-icons/io5";

import { ADVISOR_TERMINAL_NODE } from "@store/advisor";
import { IAdvisorChoice, IAdvisorEntry, IAdvisorNodePayload } from "@types";

import { AdvisorChoices } from "./AdvisorChoices";
import { AdvisorRow, UserMessage } from "./AdvisorMessage";
import { BlockList } from "./blocks/BlockRenderer";
import { ErrorBanner } from "./ErrorBanner";
import { Loader } from "./Loader";
import { ThinkingSequence } from "./ThinkingSequence";

export function AdvisorMessageList({
  entries,
  choices,
  busy,
  loading,
  pending,
  error,
  onChoose,
  onRevealPending,
  onRestart,
}: {
  entries: IAdvisorEntry[];
  choices: IAdvisorChoice[];
  busy: boolean;
  loading: boolean;
  pending: IAdvisorNodePayload | null;
  error: string | null;
  onChoose: (choice: IAdvisorChoice) => void;
  onRevealPending: () => void;
  onRestart: () => void;
}) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  // A simple "always stick to bottom" auto-scroll — new content pushes the view down.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, busy, pending, error]);

  const lastAdvisorIndex = entries.reduce(
    (last, entry, i) => (entry.role === "advisor" ? i : last),
    -1
  );
  const lastPayload = lastAdvisorIndex >= 0 ? entries[lastAdvisorIndex] : null;
  const isComplete =
    lastPayload?.role === "advisor" &&
    lastPayload.payload.node_id === ADVISOR_TERMINAL_NODE &&
    !pending;

  // Chips live under the message that offered them, and only the newest message has any.
  const showChoices = choices.length > 0 && !busy && !pending;

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6"
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        {loading && (
          <AdvisorRow>
            <Loader variant="text-shimmer" text="Loading this session…" />
          </AdvisorRow>
        )}

        {entries.map((entry, i) => {
          if (entry.role === "user") {
            return <UserMessage key={i} text={entry.text} />;
          }

          return (
            <AdvisorRow key={i}>
              <BlockList
                blocks={entry.payload.blocks}
                nodeId={entry.payload.node_id}
              />

              {i === lastAdvisorIndex && showChoices && (
                <AdvisorChoices choices={choices} onSelect={onChoose} />
              )}
            </AdvisorRow>
          );
        })}

        {busy && !pending && (
          <AdvisorRow>
            <Loader variant="text-shimmer" text="Thinking…" />
          </AdvisorRow>
        )}

        {pending?.thinking_sequence && (
          <AdvisorRow>
            <ThinkingSequence
              key={pending.node_id}
              steps={pending.thinking_sequence}
              onDone={onRevealPending}
            />
          </AdvisorRow>
        )}

        {error && <ErrorBanner message={error} />}

        {isComplete && (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={onRestart}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100"
            >
              <IoRefresh className="h-3.5 w-3.5" />
              Start another session
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
